import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Globe, 
  MapPin, 
  Building, 
  ChevronRight,
} from 'lucide-react';



const FinancialInitiativesShowcase = () => {
  const navigate = useNavigate();
  
 const projects = {
  zones: [
    {
      name: 'UEMOA',
      type: 'Zone Économique',
      description: 'Union Économique et Monétaire Ouest-Africaine',
      gradient: 'from-[#017D7E] to-[#00A99D]',
      initiatives: [
        { name: 'Blockchain Trade Finance', link: '/rd-showcase/UEMOA/BlockchainTradeFinance' },
        { name: 'RTGS Analysis', link: '/rd-showcase/UEMOA/UemoaRTGSAnalysis' },
        { name: 'Open Banking', link: '/rd-showcase/UEMOA/OpenBankingUEMOA' }
      ],
      countries: ['Sénégal', 'Mali', 'Burkina Faso', 'Niger', 'Côte d\'Ivoire', 'Guinée-Bissau', 'Bénin', 'Togo']
    },
    {
      name: 'CEMAC',
      type: 'Zone Économique',
      description: 'Communauté Économique et Monétaire de l\'Afrique Centrale',
      gradient: 'from-emerald-600 to-emerald-400',
      initiatives: [
        { name: 'GIMAC Analysis', link: '/rd-showcase/CEMAC/GIMACAnalysis' },
        { name: 'Digital Finance Framework', link: '/rd-showcase/CEMAC/CEMACFinanceAnalysis' }
      ],
      countries: ['Cameroun', 'RCA', 'Tchad', 'Congo', 'Guinée Équatoriale', 'Gabon']
    }
  ],
  countries: [
    {
      name: 'Kenya',
      type: 'National',
      description: 'Programme d\'innovation financière nationale',
      gradient: 'from-purple-600 to-purple-400',
      initiatives: [
        { name: 'Digital Finance', link: '/rd-showcase/Kenya/KenyaDigitalFinance' }
      ]
    },
    {
      name: 'Côte d\'Ivoire',
      type: 'National',
      description: 'Programme de transformation digitale',
      gradient: 'from-blue-600 to-blue-400',
      initiatives: [
        { name: 'Digital Finance', link: '/rd-showcase/CI/CIDigitalFinance' }
      ]
    },
    {
      name: 'Sénégal',
      type: 'National',
      description: 'Programme Smart Sénégal',
      gradient: 'from-green-600 to-green-400',
      initiatives: [
        { name: 'Smart Sénégal', link: '/rd-showcase/Senegal/SmartSenegalAnalysis' }
      ]
    },
    {
      name: 'Mali',
      type: 'National',
      description: 'Infrastructure financière digitale',
      gradient: 'from-yellow-600 to-yellow-400',
      initiatives: [
        { name: 'Digital Mali', link: '/rd-showcase/Mali/DigitalMaliAnalysis' }
      ]
    },
    {
      name: 'Maroc',
      type: 'National',
      description: 'Initiative Open Banking nationale',
      gradient: 'from-red-600 to-red-400',
      initiatives: [
        { name: 'Open Banking', link: '/rd-showcase/UEMOA/OpenBankingMaroc' }
      ]
    }
  ],
  worldwide: [
    {
      name: 'CBPR+',
      type: 'Mondial',
      description: 'Cross-Border Payments & Reporting Plus',
      gradient: 'from-indigo-600 to-indigo-400',
      initiatives: [
        { name: 'Standards Analysis', link: '/rd-showcase/World/CBPRPlusAnalysis' }
      ]
    }
  ]
};

  const CardIcon = ({ type }) => {
    switch (type) {
      case 'Zone Économique':
        return <Building className="w-6 h-6" />;
      case 'National':
        return <MapPin className="w-6 h-6" />;
      case 'Mondial':
        return <Globe className="w-6 h-6" />;
      default:
        return null;
    }
  };

  const ProjectCard = ({ project }) => (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white/5 rounded-xl border border-white/10 overflow-hidden 
                hover:border-green-500/50 transition-all duration-300"
    >
      <div className={`h-1 bg-gradient-to-r ${project.gradient}`} />
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${project.gradient} text-white`}>
              <CardIcon type={project.type} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{project.name}</h3>
              <p className="text-sm text-white/70">{project.type}</p>
            </div>
          </div>
        </div>

        <p className="text-white/70 mb-4">
          {project.description}
        </p>

        {project.countries && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-white/50 mb-2">Pays membres</h4>
            <div className="flex flex-wrap gap-2">
              {project.countries.map(country => (
                <span key={country} 
                      className="px-2 py-1 bg-white/5 rounded-full text-xs text-white/70
                               border border-white/10">
                  {country}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
        
{project.initiatives.map((initiative, idx) => (
  <div
    key={idx}
    onClick={() => navigate(initiative.link)}
    className="flex items-center justify-between p-2 rounded-lg 
               hover:bg-white/5 group border border-transparent
               hover:border-white/10 cursor-pointer"
  >
    <span className="text-white/70 group-hover:text-white">
      {initiative.name}
    </span>
    <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-white" />
  </div>
))}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.15),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Innovations Financières
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              en Afrique
            </span>
          </h1>
          <p className="text-xl text-white/70">
            Explorer les initiatives majeures de transformation digitale
          </p>
        </div>

        <div className="space-y-16">
          {/* Zones Économiques */}
          <section>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Building className="w-6 h-6 text-[#017D7E]" />
              Zones Économiques
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.zones.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </section>

          {/* Pays */}
          <section>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-[#017D7E]" />
              Initiatives Nationales
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.countries.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </section>

          {/* Mondial */}
          <section>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#017D7E]" />
              Standards Internationaux
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.worldwide.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FinancialInitiativesShowcase;